import type {
  BenefitsInformation,
  BenefitsRelatedEntity,
  EligibilityCheckRequestContent,
  EligibilityCheckResponseContent,
} from "@/generated/stedi/healthcare";
import { npiNumberSchema } from "@/src/models/common-schema";
import { getPersonName } from "@/src/models/person";
import type { StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";
import type { StediEligibilityStatus } from "@/src/models/stedi-eligibility-status";

export const ACTIVE_COVERAGE_CODE = "1";
export const INACTIVE_COVERAGE_CODE = "6";
export const HEALTH_BENEFIT_PLAN_SERVICE_TYPE_CODE = "30";
export const OFFICE_VISIT_SERVICE_TYPE_CODE = "98";
export const DIAGNOSTIC_X_RAY_SERVICE_TYPE_CODE = "4";
export const MRI_CAT_SCAN_SERVICE_TYPE_CODE = "62";
export const DIAGNOSTIC_MEDICAL_SERVICE_TYPE_CODE = "73";
const PCP_ENTITY_IDENTIFIER = "Primary Care Provider";
const ENTITY_IDENTIFICATION_NPI = "XX";
const MEDICAL_SERVICE_TYPE_CODES = new Set([
  OFFICE_VISIT_SERVICE_TYPE_CODE,
  DIAGNOSTIC_X_RAY_SERVICE_TYPE_CODE,
  MRI_CAT_SCAN_SERVICE_TYPE_CODE,
  DIAGNOSTIC_MEDICAL_SERVICE_TYPE_CODE,
]);

export function extractEligibilitySummary(
  response: EligibilityCheckResponseContent,
  request?: EligibilityCheckRequestContent
): {
  checkFailed: boolean;
  eligibilityStatus: StediEligibilityStatus;
  payerName: string | null;
  planType: string | null;
  planName: string | null;
  inNetwork: boolean | null;
  stediCheckId: string | null;
  tradingPartnerServiceId: string | null;
  primaryErrorCode: string | null;
  primaryErrorDescription: string | null;
} {
  const checkFailed = (Array.isArray(response.errors) && response.errors.length > 0) || !response.benefitsInformation;

  const payerName = response.payer?.name ?? null;
  const stediCheckId = response.id ?? null;
  const tradingPartnerServiceId = response.tradingPartnerServiceId ?? null;
  const primaryError = response.errors?.[0];

  if (checkFailed) {
    return {
      checkFailed: true,
      eligibilityStatus: "failed",
      payerName,
      planType: null,
      planName: null,
      inNetwork: null,
      stediCheckId,
      tradingPartnerServiceId,
      primaryErrorCode: primaryError?.code ?? null,
      primaryErrorDescription: primaryError?.description ?? null,
    };
  }

  const activeCoverage = findActiveCoverageBenefit(response);
  const planType = activeCoverage?.insuranceType?.trim() ?? null;
  const planName = activeCoverage?.planCoverage ?? null;
  const inNetwork = inNetworkFromBenefit(activeCoverage);

  let eligibilityStatus: StediEligibilityStatus = "unknown";

  if (!request || !isDentalOnlyResponseForMedicalRequest(response, request)) {
    if (response.benefitsInformation?.some((b) => b.code === ACTIVE_COVERAGE_CODE)) {
      eligibilityStatus = "active";
    } else if (response.benefitsInformation?.some((b) => b.code === INACTIVE_COVERAGE_CODE)) {
      eligibilityStatus = "inactive";
    }
  }

  return {
    checkFailed: false,
    eligibilityStatus,
    payerName,
    planType,
    planName,
    inNetwork,
    stediCheckId,
    tradingPartnerServiceId,
    primaryErrorCode: null,
    primaryErrorDescription: null,
  };
}

function findActiveCoverageBenefit(response: EligibilityCheckResponseContent) {
  const benefits = response.benefitsInformation ?? [];
  const activePlanBenefits = benefits.filter(
    (b) => b.code === ACTIVE_COVERAGE_CODE && (b.serviceTypeCodes ?? []).includes(HEALTH_BENEFIT_PLAN_SERVICE_TYPE_CODE)
  );
  // Payers can return a generic STC 30 block before a richer active block that also includes
  // STC 30. Prefer a non-dental block containing product details so response order does not
  // cause a bundled dental plan to replace the patient's medical plan.
  const activeWithNonDentalPlanStc = activePlanBenefits.find(
    (benefit) => hasProductDetails(benefit) && !isExplicitlyDentalBenefit(benefit)
  );
  const activeWithNonDentalProductDetails = benefits.find(
    (benefit) =>
      benefit.code === ACTIVE_COVERAGE_CODE && hasProductDetails(benefit) && !isExplicitlyDentalBenefit(benefit)
  );
  const activeWithPlanStc = activePlanBenefits.find(hasProductDetails);
  const activeWithProductDetails = benefits.find(
    (benefit) => benefit.code === ACTIVE_COVERAGE_CODE && hasProductDetails(benefit)
  );

  if (activeWithNonDentalPlanStc) return activeWithNonDentalPlanStc;
  if (activeWithNonDentalProductDetails) return activeWithNonDentalProductDetails;
  if (activeWithPlanStc) return activeWithPlanStc;
  if (activeWithProductDetails) return activeWithProductDetails;
  if (activePlanBenefits[0]) return activePlanBenefits[0];

  return benefits.find((b) => b.code === ACTIVE_COVERAGE_CODE);
}

function hasProductDetails(benefit: BenefitsInformation) {
  return Boolean(benefit.planCoverage?.trim() || benefit.insuranceType?.trim());
}

function isExplicitlyDentalBenefit(benefit: BenefitsInformation): boolean {
  return [benefit.planCoverage, benefit.insuranceType].some((value) => /\bdental\b/i.test(value ?? ""));
}

function isDentalOnlyResponseForMedicalRequest(
  response: EligibilityCheckResponseContent,
  request: EligibilityCheckRequestContent
): boolean {
  if (!(request.encounter?.serviceTypeCodes ?? []).some((code) => MEDICAL_SERVICE_TYPE_CODES.has(code))) {
    return false;
  }

  const coverageBenefits = (response.benefitsInformation ?? []).filter(
    (benefit) => benefit.code === ACTIVE_COVERAGE_CODE || benefit.code === INACTIVE_COVERAGE_CODE
  );

  if (coverageBenefits.length === 0) return false;

  return (
    [response.payer?.name, response.payer?.payorIdentification].some((value) => /\bdental\b/i.test(value ?? "")) ||
    coverageBenefits.every(isExplicitlyDentalBenefit)
  );
}

export function getMedicalEligibilityRetryTradingPartnerId(
  response: EligibilityCheckResponseContent,
  request: EligibilityCheckRequestContent
): string | null {
  if (!isDentalOnlyResponseForMedicalRequest(response, request)) return null;

  for (const benefit of response.benefitsInformation ?? []) {
    const homePlan = (benefit.benefitsRelatedEntities ?? []).find(
      (entity) => entity.entityIdentifier === "Party Performing Verification" && entity.entityIdentification === "PI"
    );
    const payerId = homePlan?.entityIdentificationValue?.trim();

    if (payerId && payerId !== request.tradingPartnerServiceId) return payerId;
  }

  return null;
}

function firstNonEmptyDate(...values: Array<string | undefined>): string | null {
  return values.map((value) => value?.trim()).find((value) => Boolean(value)) ?? null;
}

export function extractCoverageExpirationDate(response: EligibilityCheckResponseContent): string | null {
  const benefits = response.benefitsInformation ?? [];
  const inactiveBenefit = benefits.find((b) => b.code === INACTIVE_COVERAGE_CODE);
  const inactiveDates = inactiveBenefit?.benefitsDateInformation;

  return (
    firstNonEmptyDate(
      inactiveDates?.eligibilityEnd,
      inactiveDates?.planEnd,
      inactiveDates?.policyExpiration,
      inactiveDates?.benefitEnd
    ) ??
    firstNonEmptyDate(
      response.planDateInformation?.eligibilityEnd,
      response.planDateInformation?.planEnd,
      response.planDateInformation?.policyExpiration
    ) ??
    firstNonEmptyDate(response.subscriber?.endDateTimePeriod)
  );
}

function inNetworkFromBenefit(benefit: ReturnType<typeof findActiveCoverageBenefit>): boolean | null {
  if (benefit?.inPlanNetworkIndicatorCode === "Y" || benefit?.inPlanNetworkIndicator === "Yes") {
    return true;
  }

  if (benefit?.inPlanNetworkIndicatorCode === "N" || benefit?.inPlanNetworkIndicator === "No") {
    return false;
  }

  return null;
}

export type StediPcpDisplayItem = {
  name: string | null;
  npi: string | null;
  dates: string | null;
  isActive: boolean;
};

export type StediEligibilityDisplay = {
  checkFailed: boolean;
  eligibilityStatus: StediEligibilityStatus;
  payerName: string | null;
  planType: string | null;
  planName: string | null;
  inNetwork: boolean | null;
  pcps: StediPcpDisplayItem[];
  officeVisitRequiresAuth: boolean | null;
  officeVisitAuthReason: string | null;
  primaryErrorCode: string | null;
  primaryErrorDescription: string | null;
};

function formatX12Date(dateStr: string): string {
  const digits = dateStr.trim();

  if (digits.length === 8 && /^\d{8}$/.test(digits)) {
    return `${digits.slice(4, 6)}/${digits.slice(6, 8)}/${digits.slice(0, 4)}`;
  }

  return digits;
}

function parsePcpDateRange(rawDate: string | null | undefined): { dates: string | null; isActive: boolean } {
  if (!rawDate?.trim()) {
    return { dates: null, isActive: true };
  }

  const trimmed = rawDate.trim();

  if (trimmed.includes("-")) {
    const [start, end] = trimmed.split("-").map((s) => s.trim());
    const isOpenEnded = !end || end.startsWith("9999");
    const formattedStart = start ? formatX12Date(start) : null;
    const formattedEnd = isOpenEnded ? "Present" : formatX12Date(end);

    const todayYyyyMmDd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const isExpired = !isOpenEnded && /^\d{8}$/.test(end) && end < todayYyyyMmDd;

    const dates = formattedStart ? `${formattedStart} – ${formattedEnd}` : formattedEnd;

    return { dates, isActive: !isExpired };
  }

  return { dates: formatX12Date(trimmed), isActive: true };
}

function extractPcpNpi(entity: BenefitsRelatedEntity): string | null {
  if (entity.entityIdentification !== ENTITY_IDENTIFICATION_NPI) {
    return null;
  }

  const parsed = npiNumberSchema.safeParse(entity.entityIdentificationValue ?? "");

  return parsed.success ? parsed.data : null;
}

function extractAllPcpsFromEligibilityResponse(response: EligibilityCheckResponseContent): StediPcpDisplayItem[] {
  const pcps: StediPcpDisplayItem[] = [];

  for (const benefit of response.benefitsInformation ?? []) {
    const isPcpBenefit =
      benefit.code === "L" ||
      benefit.name === PCP_ENTITY_IDENTIFIER ||
      (benefit.benefitsRelatedEntities ?? []).some((e) => e.entityIdentifier === PCP_ENTITY_IDENTIFIER) ||
      benefit.benefitsRelatedEntity?.entityIdentifier === PCP_ENTITY_IDENTIFIER;

    if (!isPcpBenefit) continue;

    const rawDate =
      benefit.benefitsDateInformation?.primaryCareProvider ||
      benefit.benefitsDateInformation?.benefitBegin ||
      benefit.benefitsDateInformation?.benefit ||
      null;

    const { dates, isActive } = parsePcpDateRange(rawDate);
    const entities = benefit.benefitsRelatedEntities?.length
      ? benefit.benefitsRelatedEntities
      : benefit.benefitsRelatedEntity
        ? [benefit.benefitsRelatedEntity]
        : [];

    for (const entity of entities) {
      if (entity.entityIdentifier === PCP_ENTITY_IDENTIFIER) {
        const rawName =
          [entity.entityFirstname, entity.entityMiddlename, entity.entityName, entity.entitySuffix]
            .map((part) => part?.trim())
            .filter(Boolean)
            .join(" ") || null;
        const npi = extractPcpNpi(entity);
        const name = npi
          ? getPersonName({
              givenName: entity.entityFirstname?.trim() || null,
              middleName: entity.entityMiddlename?.trim() || null,
              familyName: entity.entityName?.trim() || null,
            }) || rawName
          : rawName;

        if (name || npi) {
          pcps.push({
            name: name || rawName,
            npi,
            dates,
            isActive,
          });
        }
      }
    }
  }

  const unique = pcps.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (t) => t.name === item.name && t.npi === item.npi && t.dates === item.dates && t.isActive === item.isActive
      )
  );

  return unique.sort((a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0));
}

export function extractPcpNpiFromEligibilityResponse(response: EligibilityCheckResponseContent): string | null {
  const pcps = extractAllPcpsFromEligibilityResponse(response);

  return pcps.find((p) => p.isActive && p.npi)?.npi ?? pcps.find((p) => p.npi)?.npi ?? null;
}

export function resolveStediEligibilityDisplay(check: StediEligibilityCheck): StediEligibilityDisplay {
  const response = check.responseJson as EligibilityCheckResponseContent;
  const fromResponse = extractEligibilitySummary(response);

  return {
    checkFailed: check.checkFailed ?? fromResponse?.checkFailed ?? false,
    eligibilityStatus: check.eligibilityStatus ?? fromResponse?.eligibilityStatus ?? "unknown",
    payerName: check.payerName ?? fromResponse?.payerName ?? null,
    planType: fromResponse.planType ?? check.planType ?? null,
    planName: fromResponse.planName ?? check.planName ?? null,
    inNetwork: check.inNetwork ?? fromResponse?.inNetwork ?? null,
    pcps: extractAllPcpsFromEligibilityResponse(response),
    officeVisitRequiresAuth: check.officeVisitRequiresAuth,
    officeVisitAuthReason: check.officeVisitAuthReason,
    primaryErrorCode: check.primaryErrorCode ?? fromResponse?.primaryErrorCode ?? null,
    primaryErrorDescription: check.primaryErrorDescription ?? fromResponse?.primaryErrorDescription ?? null,
  };
}
